function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function numberValue(value) {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function booleanValue(value) {
  if (typeof value === "string") return value.toLowerCase() !== "false";
  return Boolean(value);
}

export function normalizePublicResults(payload) {
  const source = asObject(payload);
  const metadata = asObject(source.metadata || source.meta);
  const participation = asObject(firstValue(metadata.participation, source.participation));
  const resultsAvailable = booleanValue(
    firstValue(
      metadata.resultsAvailable,
      source.resultsAvailable,
      metadata.published,
      source.published,
      source.publishedAt
    )
  );
  const outcome = asObject(
    firstValue(source.outcome, source.outcomePayload, metadata.outcome, metadata.outcomePayload)
  );

  const contests = asArray(outcome.contests).map((contest, contestIndex) => {
    const contestItem = asObject(contest);
    const contestKey = String(
      firstValue(
        contestItem.contestKey,
        contestItem.key,
        contestItem.code,
        contestItem._id,
        `contest-${contestIndex + 1}`
      )
    );
    const options = asArray(contestItem.options).map((option, optionIndex) => {
      const optionItem = asObject(option);
      const votes = numberValue(
        firstValue(
          optionItem.votes,
          optionItem.voteCount,
          optionItem.votesCount,
          optionItem.totalVoteCount,
          optionItem.count,
          optionItem.totalVotes
        )
      );
      return {
        optionKey: String(
          firstValue(
            optionItem.optionKey,
            optionItem.key,
            optionItem.code,
            optionItem._id,
            `${contestKey}-option-${optionIndex + 1}`
          )
        ),
        label: String(
          firstValue(
            optionItem.label,
            optionItem.title,
            optionItem.name,
            optionItem.optionName,
            `Opción ${optionIndex + 1}`
          )
        ),
        votes,
        percentage: numberValue(
          firstValue(
            optionItem.percentage,
            optionItem.percent,
            optionItem.share,
            optionItem.votePercentage
          )
        ),
      };
    });
    const totalVotes = options.reduce((total, option) => total + (option.votes || 0), 0);

    return {
      contestKey,
      title: String(
        firstValue(contestItem.title, contestItem.label, contestItem.name, contestKey)
      ),
      options: options.map((option) => ({
        ...option,
        percentage:
          option.percentage !== null
            ? option.percentage
            : totalVotes > 0 && option.votes !== null
              ? (option.votes * 100) / totalVotes
              : null,
      })),
    };
  });

  return {
    metadata: {
      title: String(firstValue(metadata.title, source.title, source.name, "")),
      typeKey: String(firstValue(metadata.typeKey, source.typeKey, source.type, "")),
      participationPercentage: numberValue(
        firstValue(
          participation.percentage,
          participation.percent,
          participation.participation,
          source.participationPercentage,
          source.percentage
        )
      ),
      totalVotes: numberValue(
        firstValue(
          metadata.totalVotes,
          source.totalVotes,
          participation.submitted,
          participation.submittedCount
        )
      ),
      closesAt: firstValue(metadata.closesAt, source.closesAt, source.closedAt) || null,
      availableAt:
        firstValue(
          metadata.availableAt,
          source.availableAt,
          source.resultsPublishAt,
          metadata.resultsPublishAt
        ) || null,
      publishedAt: firstValue(metadata.publishedAt, source.publishedAt) || null,
      resultsAvailable,
      integrityHash: source.integrityHash || metadata.integrityHash || "",
    },
    outcome: { contests },
  };
}

export function normalizeVotingVerification(payload) {
  const source = asObject(payload);
  return {
    verified: booleanValue(source.verified),
    valid: booleanValue(source.valid),
    resultsAvailable: booleanValue(
      firstValue(source.resultsAvailable, source.published, source.publishedAt)
    ),
    metadata: {
      title: String(source.title || ""),
      typeKey: String(source.typeKey || source.type || ""),
      publishedAt: source.publishedAt || null,
      availableAt: source.availableAt || source.resultsPublishAt || null,
      hash: String(source.hash || ""),
      integrityHash: String(source.integrityHash || ""),
    },
  };
}

export function formatVotingNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("es-MX").format(number) : "0";
}

export function formatVotingPercent(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(1)}%` : "0%";
}
